import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Divider,
  Stack,
  Typography,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';

import { AccountSummary } from './account-summary.component';
import { ChannelsCard } from './channels-card.component';
import { SubscriptionCard } from './subscription-card.component';

import { AddOnsCard, formatShortDate } from '@/components/billing';
import { Company, CreditLine, PlanPurchase } from '@/services/hsm-backend';

export interface CompanyDetailsProps {
  company: Company;
  refetch: () => Promise<void>;
}

/**
 * Body of the "Empresa" tab. Renders the read-only dashboard for the
 * company: summary KPIs, subscription card, active plan, add-ons,
 * credit line and channels. Receives the loaded company by prop — the
 * fetch happens once at the `CompanyShell` parent route.
 *
 * Each section header exposes a CTA that navigates to its dedicated
 * billing tab (Plan / Paquete / Crédito).
 *
 * @param company Loaded company; drives the rendered dashboard.
 */
export const CompanyDetails = ({ company, refetch }: CompanyDetailsProps) => (
  <Box sx={{ maxWidth: 1080, mx: 'auto', px: 3, py: 4 }}>
    <CompanyBody company={company} refetch={refetch} />
  </Box>
);

const CompanyBody = ({
  company,
  refetch,
}: {
  company: Company;
  refetch: () => Promise<void>;
}) => {
  const navigate = useNavigate();
  const goTo = (tab: 'plan' | 'add-ons' | 'credit-line') =>
    navigate(`/company/${company.companyId}/${tab}`);

  return (
    <Stack spacing={3}>
      <AccountSummary
        subscription={company.subscription}
        companyId={company.companyId}
      />
      {company.subscription && (
        <SubscriptionCard
          subscription={company.subscription}
          companyId={company.companyId}
          refetch={refetch}
        />
      )}
      {company.subscription?.activePlan && (
        <ActivePlanCard
          plan={company.subscription.activePlan}
          onChange={() => goTo('plan')}
        />
      )}
      {company.subscription?.addOnPurchases &&
        company.subscription.addOnPurchases.length > 0 && (
          <AddOnsCard
            addOns={company.subscription.addOnPurchases}
            rightSlot={
              <Button
                variant="outlined"
                size="small"
                onClick={() => goTo('add-ons')}
              >
                + Comprar paquete
              </Button>
            }
          />
        )}
      {company.subscription?.creditLine && (
        <CreditLineCard
          creditLine={company.subscription.creditLine}
          onEdit={() => goTo('credit-line')}
        />
      )}
      <ChannelsCard companyId={company.companyId} channels={company.channels} />
    </Stack>
  );
};

interface ActivePlanCardProps {
  plan: PlanPurchase;
  onChange: () => void;
}

const ActivePlanCard = ({ plan, onChange }: ActivePlanCardProps) => (
  <Card>
    <CardContent>
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Typography variant="h6">Plan activo</Typography>
        <Button variant="outlined" size="small" onClick={onChange}>
          Cambiar
        </Button>
      </Stack>
      <Divider sx={{ my: 1 }} />
      <Typography variant="body1" fontWeight={700}>
        {plan.messageQty.toLocaleString('es-AR')} mensajes/mes
      </Typography>
      <Typography variant="caption" color="text.secondary">
        Contratado el {formatShortDate(plan.createdAt)}
      </Typography>
    </CardContent>
  </Card>
);

interface CreditLineCardProps {
  creditLine: CreditLine;
  onEdit: () => void;
}

const CreditLineCard = ({ creditLine, onEdit }: CreditLineCardProps) => (
  <Card>
    <CardContent>
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Typography variant="h6">Línea de crédito</Typography>
        <Chip
          label={creditLine.status === 'active' ? 'Activa' : 'Inactiva'}
          color={creditLine.status === 'active' ? 'success' : 'default'}
          size="small"
        />
      </Stack>
      <Divider sx={{ my: 1 }} />
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="flex-end"
      >
        <Box>
          <Typography variant="body1" fontWeight={700}>
            {creditLine.messageQty.toLocaleString('es-AR')} mensajes disponibles
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Creada el {formatShortDate(creditLine.createdAt)}
          </Typography>
        </Box>
        <Button variant="outlined" size="small" onClick={onEdit}>
          Editar
        </Button>
      </Stack>
    </CardContent>
  </Card>
);
